import React from 'react';

import { Typography } from '@mui/material';
import type { LucideIcon } from 'lucide-react';
import { Check, PenLine, Signature } from 'lucide-react';
import type { SigningStatusState } from 'src/components/workflow/workflow-list/signing-status';
import { Apollo } from 'src/gql/generated';
import { getWorkflowStateColor, getWorkflowStateDisplayString } from 'src/workflow';
import { makeStyles } from 'tss-react/mui';

const SIGNING_STATUS_ICONS: Record<SigningStatusState, LucideIcon> = {
  [Apollo.WorkflowState.ReadyForSignature]: PenLine,
  [Apollo.WorkflowState.Signing]: Signature,
  [Apollo.WorkflowState.Signed]: Check,
};

const useStyles = makeStyles<{ backgroundColor: string }>()((theme, { backgroundColor }) => ({
  container: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
    maxWidth: '100%',
  },
  icon: {
    alignItems: 'center',
    backgroundColor,
    borderRadius: '50%',
    boxSizing: 'border-box',
    color: theme.palette.common.white,
    display: 'flex',
    flexShrink: 0,
    height: 16,
    justifyContent: 'center',
    width: 16,
  },
  label: {
    color: backgroundColor,
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 600,
  },
}));

interface SigningStatusBadgeProps {
  state: SigningStatusState;
  showLabel?: boolean;
}

/** Compact solid badge for Ready for Signature / Signing / Signed. */
export function SigningStatusBadge({
  showLabel = true,
  state,
}: SigningStatusBadgeProps): JSX.Element {
  const backgroundColor = getWorkflowStateColor(state);
  const { classes } = useStyles({ backgroundColor });
  const Icon = SIGNING_STATUS_ICONS[state];

  return (
    <div className={classes.container}>
      <span className={classes.icon}>
        <Icon size={10} strokeWidth={2.75} />
      </span>
      {showLabel && (
        <Typography noWrap className={classes.label} variant="body2">
          {getWorkflowStateDisplayString(state)}
        </Typography>
      )}
    </div>
  );
}
